/**
 * Kostenarten-Katalog nach BetrKV § 2 mit Standard-Umlageschlüssel.
 *
 * Jede Kostenart trägt ihren Gültigkeitszeitraum, damit Altzeiträume korrekt
 * abgerechnet werden können (Beispiel: Gemeinschaftsantenne/Breitbandkabel ist für
 * Zeiträume ab 01.07.2024 nicht mehr als Betriebskosten umlagefähig, TKG-Novelle).
 */

import type { ISODate, Schluessel } from '@nkp/shared';

export interface Kostenart {
  code: string;
  /** Nummer nach BetrKV § 2 */
  nummer: string;
  bezeichnung: string;
  standardSchluessel: Schluessel;
  /** Letzter Tag, an dem die Kostenart umlagefähig ist. null = unbefristet. */
  umlagefaehigBis: ISODate | null;
  hinweis?: string;
}

export const KOSTENARTEN: readonly Kostenart[] = [
  {
    code: 'grundsteuer',
    nummer: '1',
    bezeichnung: 'Grundsteuer',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'wasser',
    nummer: '2',
    bezeichnung: 'Wasserversorgung',
    standardSchluessel: 'verbrauch',
    umlagefaehigBis: null,
    hinweis: 'Bei Zählern verbrauchsabhängig, sonst nach Wohnfläche.',
  },
  {
    code: 'entwaesserung',
    nummer: '3',
    bezeichnung: 'Entwässerung',
    standardSchluessel: 'verbrauch',
    umlagefaehigBis: null,
  },
  {
    code: 'aufzug',
    nummer: '7',
    bezeichnung: 'Aufzug',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'strassenreinigung',
    nummer: '8',
    bezeichnung: 'Straßenreinigung und Müllbeseitigung',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'gebaeudereinigung',
    nummer: '9',
    bezeichnung: 'Gebäudereinigung und Ungezieferbekämpfung',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'gartenpflege',
    nummer: '10',
    bezeichnung: 'Gartenpflege',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'beleuchtung',
    nummer: '11',
    bezeichnung: 'Beleuchtung (Allgemeinstrom)',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'schornsteinreinigung',
    nummer: '12',
    bezeichnung: 'Schornsteinreinigung',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'versicherung',
    nummer: '13',
    bezeichnung: 'Sach- und Haftpflichtversicherung',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'hausmeister',
    nummer: '14',
    bezeichnung: 'Hausmeister',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
  },
  {
    code: 'antenne_kabel',
    nummer: '15',
    bezeichnung: 'Gemeinschaftsantenne / Breitbandkabel',
    standardSchluessel: 'einheiten',
    umlagefaehigBis: '2024-06-30',
    hinweis:
      'Für Abrechnungszeiträume ab 01.07.2024 nicht mehr als Betriebskosten umlagefähig (TKG-Novelle).',
  },
  {
    code: 'waeschepflege',
    nummer: '16',
    bezeichnung: 'Maschinelle Wascheinrichtung',
    standardSchluessel: 'einheiten',
    umlagefaehigBis: null,
  },
  {
    code: 'sonstige',
    nummer: '17',
    bezeichnung: 'Sonstige Betriebskosten',
    standardSchluessel: 'wohnflaeche',
    umlagefaehigBis: null,
    hinweis: 'Nur umlagefähig, wenn im Mietvertrag konkret benannt.',
  },
];

export const SCHLUESSEL_BEZEICHNUNG: Record<Schluessel, string> = {
  wohnflaeche: 'Wohnfläche (m²)',
  personen: 'Personenzahl',
  einheiten: 'Wohneinheiten',
  verbrauch: 'Verbrauch (Zähler)',
  meg: 'Miteigentumsanteil',
};

export const SCHLUESSEL_ERLAEUTERUNG: Record<Schluessel, string> = {
  wohnflaeche: 'Verteilung nach Quadratmetern der Einheit, tagegenau zeitgewichtet.',
  personen: 'Verteilung nach gemeldeter Personenzahl, tagegenau zeitgewichtet.',
  einheiten: 'Gleichmäßige Verteilung auf alle Einheiten, tagegenau zeitgewichtet.',
  verbrauch: 'Verteilung nach abgelesenem Verbrauch der Einheit.',
  meg: 'Verteilung nach Miteigentumsanteilen (WEG).',
};

export function findeKostenart(code: string): Kostenart | undefined {
  return KOSTENARTEN.find((k) => k.code === code);
}

/** Prüft, ob eine Kostenart im gewählten Abrechnungszeitraum umlagefähig ist. */
export function istUmlagefaehigImZeitraum(kostenart: Kostenart, bis: ISODate): boolean {
  return kostenart.umlagefaehigBis === null || bis <= kostenart.umlagefaehigBis;
}
